import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { api, fileUrl } from "@/lib/api";
import { formatCAD } from "@/lib/format";
import { Star, ShoppingBag, Heart, Truck, Shield, ChevronLeft } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";
import PODCustomizer from "@/components/PODCustomizer";

export default function ProductDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { add } = useCart();
  const [data, setData] = useState(null);
  const [qty, setQty] = useState(1);
  const [customText, setCustomText] = useState("");
  const [podPayload, setPodPayload] = useState(null);

  useEffect(() => {
    api.get(`/products/by-slug/${slug}`).then(({ data }) => setData(data));
  }, [slug]);

  if (!data) return <Layout><div className="p-16 text-center text-[#5C5A56]">Loading…</div></Layout>;
  const { product, merchant, reviews } = data;

  const handleAdd = async () => {
    if (product.product_type === "POD" && !podPayload) {
      toast.error("Please upload artwork and confirm placement before adding to cart.");
      return;
    }
    if (product.product_type === "CUSTOM" && !customText) {
      toast.error("Please enter your custom text.");
      return;
    }
    await add({
      product_id: product.product_id,
      quantity: qty,
      pod_payload: podPayload,
      custom_text: customText || undefined,
    });
    toast.success("Added to cart");
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        <button onClick={() => nav(-1)} className="btn-ghost inline-flex items-center gap-1 text-sm">
          <ChevronLeft className="w-4 h-4" strokeWidth={1.5} /> Back
        </button>
      </div>
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-10 pb-12">
        {/* GALLERY */}
        <div className="md:col-span-6" data-testid="product-gallery">
          <div className="aspect-square overflow-hidden surface">
            <img src={product.featured_image || product.images[0]} alt={product.name} className="w-full h-full object-cover" />
          </div>
        </div>

        {/* INFO */}
        <div className="md:col-span-6">
          <Link to={`/store/${merchant?.slug}`} className="overline hover:text-[#702A32]" data-testid="product-store-link">
            {merchant?.name}
          </Link>
          <h1 className="font-heading text-4xl md:text-5xl mt-2 tracking-tight" data-testid="product-name">{product.name}</h1>

          <div className="flex items-center gap-3 mt-3 text-sm text-[#5C5A56]">
            <span className="inline-flex items-center gap-1"><Star className="w-4 h-4 fill-[#C78B1B] text-[#C78B1B]" strokeWidth={1.5} /> {product.rating?.toFixed(1)} ({product.review_count})</span>
            <span>·</span>
            <span>{product.sales_count} sold</span>
          </div>

          <div className="mt-6 flex items-baseline gap-3">
            <div className="font-mono text-3xl text-[#1A1A18]" data-testid="product-price">{formatCAD(product.price)}</div>
            {product.compare_price && (
              <div className="font-mono text-lg text-[#8A8782] line-through">{formatCAD(product.compare_price)}</div>
            )}
          </div>

          <p className="text-[#5C5A56] leading-relaxed font-light mt-6">{product.description}</p>

          {product.product_type === "CUSTOM" && (
            <div className="mt-6">
              <label className="block text-sm font-medium mb-1.5">Your custom text</label>
              <input className="input-field" value={customText} onChange={(e) => setCustomText(e.target.value)} placeholder="e.g. The Brown Family · Est. 2018" data-testid="custom-text-input" />
            </div>
          )}

          {product.product_type === "POD" && product.pod_config && (
            <div className="mt-8" data-testid="pod-customizer-wrapper">
              <h3 className="font-heading text-2xl mb-3">Design your product</h3>
              <PODCustomizer
                productId={product.product_id}
                podConfig={product.pod_config}
                onChange={setPodPayload}
              />
            </div>
          )}

          <div className="flex items-center gap-3 mt-8">
            <div className="flex items-center border border-[#E5E3DD]">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-4 py-3" data-testid="qty-minus">−</button>
              <span className="px-4 font-mono">{qty}</span>
              <button onClick={() => setQty(qty + 1)} className="px-4 py-3" data-testid="qty-plus">+</button>
            </div>
            <button onClick={handleAdd} className="btn-primary inline-flex items-center gap-2 flex-1 justify-center" data-testid="add-to-cart-btn">
              <ShoppingBag className="w-4 h-4" strokeWidth={1.5} /> Add to cart
            </button>
            <button className="btn-secondary" data-testid="wishlist-btn"><Heart className="w-4 h-4" strokeWidth={1.5} /></button>
          </div>

          <div className="grid grid-cols-2 gap-4 mt-8 text-sm">
            <div className="flex items-start gap-3">
              <Truck className="w-5 h-5 text-[#2A4B3A] mt-0.5" strokeWidth={1.5} />
              <div>
                <div className="font-medium">Ships from Canada</div>
                <div className="text-[#8A8782]">2–5 business days</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-[#702A32] mt-0.5" strokeWidth={1.5} />
              <div>
                <div className="font-medium">Verified merchant</div>
                <div className="text-[#8A8782]">Buyer protection</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Reviews */}
      <section className="border-t border-[#E5E3DD] py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-heading text-3xl tracking-tight mb-8">Customer reviews</h2>
          <div className="grid md:grid-cols-2 gap-6" data-testid="product-reviews">
            {reviews?.length ? reviews.slice(0, 8).map((r) => (
              <div key={r.review_id} className="surface p-5">
                <div className="flex items-center gap-2">
                  <div className="flex">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} className={`w-4 h-4 ${i < r.rating ? "fill-[#C78B1B] text-[#C78B1B]" : "text-[#E5E3DD]"}`} strokeWidth={1.5} />
                    ))}
                  </div>
                  <span className="text-sm text-[#5C5A56]">{r.user_name}</span>
                </div>
                <p className="text-sm text-[#5C5A56] font-light mt-3 leading-relaxed">{r.body}</p>
              </div>
            )) : <p className="text-[#5C5A56]">No reviews yet — be the first!</p>}
          </div>
        </div>
      </section>
    </Layout>
  );
}
